import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link, useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '../context/AuthContext';
import Breadcrumbs from '../components/Breadcrumbs';
import InspectionBanner from '../components/InspectionBanner';
import { StatCard, StatusBar, StatChips, DamageGauge, damageTone } from '../components/metrics';
import LineChart from '../components/LineChart';
import { PeriodFilter, ActivityRow, periodLabel } from '../components/PeriodFilter';
import GroupedProofGallery from '../components/GroupedProofGallery';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { SITE_STATUS, POSITIONS } from '../lib/status';
import { Building2, Boxes, Plus, Loader2, Pencil, Trash2, ArrowRight, Layers, TriangleAlert, TrendingUp, Images } from 'lucide-react';
import { toast } from 'sonner';

const ALL_POS = ['poster_left', 'poster_right', 'door_vinyl', 'door_side'];

export default function TowerDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const [params] = useSearchParams();
  const inspection = params.get('inspection');
  const inspQ = inspection ? `?inspection=${inspection}` : '';
  const canEdit = ['admin', 'manager'].includes(user.role);
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [name, setName] = useState('');
  const [pos, setPos] = useState([...ALL_POS]);
  const [edit, setEdit] = useState(null);
  const [trends, setTrends] = useState(null);
  const [days, setDays] = useState(30);
  const [proofs, setProofs] = useState(null);

  const load = useCallback(() => api.get(`/towers/${id}/detail?days=${days}`).then((r) => setData(r.data)), [id, days]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get(`/towers/${id}/trends?days=${days || 90}`).then((r) => setTrends(r.data.series)).catch(() => {}); }, [id, days]);
  useEffect(() => { api.get(`/proofs?tower_id=${id}&limit=1000`).then((r) => setProofs(r.data.proofs)).catch(() => setProofs([])); }, [id]);

  const addLift = async () => { if (!name) return toast.error('Lift name required'); await api.post(`/towers/${id}/lifts`, { name, positions: pos }); setName(''); load(); toast.success('Lift + sites created'); };
  const saveEdit = async () => { await api.patch(`/lifts/${edit.id}`, { name: edit.name }); setEdit(null); load(); toast.success('Renamed'); };
  const delLift = async (l) => { if (!window.confirm(`Delete ${l.name} and its sites?`)) return; await api.delete(`/lifts/${l.id}`); load(); toast.success('Lift deleted'); };

  if (!data) return <Loader2 className="h-6 w-6 animate-spin text-primary" />;
  const { tower, society, stats, lifts, activity } = data;

  return (
    <div className="space-y-5">
      {inspection && <InspectionBanner inspectionId={inspection} />}
      <Breadcrumbs items={[{ label: 'Societies', to: '/societies' }, { label: society.name, to: `/societies/${society.id}${inspQ}` }, { label: tower.name }]} />
      <div>
        <p className="overline text-primary flex items-center gap-2"><Building2 className="h-3.5 w-3.5" /> {society.name} · Tower</p>
        <h1 className="font-head text-3xl font-bold">{tower.name}</h1>
      </div>

      <div className="grid gap-4 lg:grid-cols-4">
        <div className="grid grid-cols-2 gap-3 lg:col-span-3">
          <StatCard label="Sites" value={stats.total} sub={`${stats.installed} deployed`} icon={Layers} />
          <StatCard label="Occupancy" value={`${stats.occupancy_pct}%`} sub={`${stats.occupied}/${stats.total} occupied`} icon={Boxes} tone="text-blue-400" />
          <StatCard label="Damaged" value={stats.damaged} icon={TriangleAlert} tone={damageTone(stats.damage_pct)} />
          <StatCard label="Lifts" value={lifts.length} icon={Boxes} tone="text-primary" />
        </div>
        <div className="rounded-xl border border-border bg-card p-4"><DamageGauge stats={stats} /></div>
      </div>

      <div className="rounded-xl border border-border bg-card p-4" data-testid="tower-trends">
        <p className="mb-2 flex items-center gap-2 font-head text-lg font-bold"><TrendingUp className="h-4 w-4 text-primary" /> Trends (30 days)</p>
        {trends ? <LineChart series={trends} lines={[{ key: 'installed_cum', color: '#2563eb', label: 'Installed' }, { key: 'damage', color: '#ef4444', label: 'Damage events' }, { key: 'repair', color: '#06b6d4', label: 'Repairs' }]} /> : <div className="flex h-[150px] items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div>}
      </div>

      <div className="flex items-center justify-between">
        <p className="overline text-muted-foreground">Activity · {periodLabel(days)}</p>
        <PeriodFilter value={days} onChange={setDays} />
      </div>
      <ActivityRow activity={activity} />

      <div data-testid="tower-proof-gallery">
        <p className="mb-2 flex items-center gap-2 font-head text-xl font-bold"><Images className="h-4 w-4 text-primary" /> Proof Photos by Lift</p>
        {proofs === null ? <Loader2 className="h-5 w-5 animate-spin text-primary" /> : (
          <GroupedProofGallery proofs={proofs} allowedGroupings={['lift_only', 'site_type', 'campaign', 'action', 'operator']} initialGroupBy="lift_only" emptyMessage="No proof photos captured in this tower yet." />
        )}
      </div>

      <div className="flex items-center justify-between">
        <p className="font-head text-xl font-bold">Lifts</p>
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            <Input placeholder="Lift name" data-testid="td-lift-name" value={name} onChange={(e) => setName(e.target.value)} className="w-32" />
            <div className="hidden gap-1 md:flex">
              {ALL_POS.map((p) => (
                <label key={p} className="flex items-center gap-1 rounded border border-border px-1.5 py-1 text-[10px]">
                  <Checkbox checked={pos.includes(p)} onCheckedChange={() => setPos((prev) => prev.includes(p) ? prev.filter((x) => x !== p) : [...prev, p])} /> {POSITIONS[p].split(' ')[0]}
                </label>
              ))}
            </div>
            <Button onClick={addLift} data-testid="td-add-lift"><Plus className="h-4 w-4" /></Button>
          </div>
        )}
      </div>

      <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
        {lifts.map((l) => (
          <div key={l.id} data-testid={`tower-lift-${l.id}`} onClick={() => navigate(`/lifts/${l.id}${inspQ}`)} className="tile-press cursor-pointer rounded-xl border-2 border-border bg-card p-4 hover:border-primary">
            <div className="flex items-start justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-lg bg-secondary"><Boxes className="h-6 w-6" /></div>
                <div>
                  <p className="font-head text-lg font-bold">{l.name}</p>
                  <p className="text-xs text-muted-foreground">{l.stats.total} sites · {l.stats.damage_pct}% damage</p>
                </div>
              </div>
              <div className="flex gap-1">
                {l.sites.map((s) => <span key={s.id} className="h-2.5 w-2.5 rounded-full" style={{ background: (SITE_STATUS[s.current_status] || {}).dot }} />)}
              </div>
            </div>
            <StatusBar counts={l.stats.status_counts} total={l.stats.total} className="mt-3" />
            <div className="mt-3 flex items-center justify-between">
              {canEdit ? (
                <div className="flex gap-1">
                  <Button size="icon" variant="ghost" className="h-7 w-7" data-testid={`edit-lift-${l.id}`} onClick={(e) => { e.stopPropagation(); setEdit(l); }}><Pencil className="h-3.5 w-3.5" /></Button>
                  <Button size="icon" variant="ghost" className="h-7 w-7 text-rose-400" data-testid={`del-lift-${l.id}`} onClick={(e) => { e.stopPropagation(); delLift(l); }}><Trash2 className="h-3.5 w-3.5" /></Button>
                </div>
              ) : <span />}
              <span className="flex items-center text-xs text-primary">Open tiles <ArrowRight className="ml-1 h-3.5 w-3.5" /></span>
            </div>
          </div>
        ))}
        {!lifts.length && <p className="text-sm text-muted-foreground">No lifts yet.</p>}
      </div>

      <Dialog open={!!edit} onOpenChange={(v) => !v && setEdit(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Rename Lift</DialogTitle></DialogHeader>
          <Input value={edit?.name || ''} data-testid="edit-lift-input" onChange={(e) => setEdit({ ...edit, name: e.target.value })} />
          <DialogFooter><Button onClick={saveEdit} data-testid="save-lift-edit">Save</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
